/**
 * <custom-puzzle-creator> — le studio de la carte mots mêlés personnalisée.
 *
 * Sur la fiche produit, un point d'entrée : « Créer ma carte », puis la carte
 * prête. La création se fait dans un studio plein écran (`<dialog>`), une étape
 * par écran : la carte (occasion, format, forme), les mots, le message caché,
 * l'aperçu. La grille est fabriquée par l'edge function
 * `api-custom-puzzle-generate` ; le thème ne détient jamais qu'un aperçu
 * filigrané.
 *
 * Le bloc écrit les propriétés de ligne dans le formulaire produit d'Horizon.
 * Ce qui part au panier est toujours ce que l'acheteur a vu : toute
 * modification de la grille efface la carte enregistrée, et le titre ou la
 * signature ne passent qu'avec un aperçu à jour.
 *
 * La saisie est gardée en `localStorage` : fermer le studio ou quitter la page
 * ne fait rien perdre.
 */
(() => {
  /* ---- Règles du produit (miroir de l'edge function) ------------------- */

  const MIN_WORDS = 5;
  /** La carte postale a une grille fixe : au-delà de 16 mots, sa liste déborde du recto. */
  const MAX_WORDS = { "card-a5": 18, "postcard-a6": 16 };
  const MIN_LENGTH = 3;
  const MAX_LENGTH = 12;
  const MIN_HIDDEN = 3;
  const MAX_HIDDEN = 40;
  const MAX_TEXT = 60;

  /** En dessous, la fabrication a l'air d'un bug plutôt que d'un artisanat. */
  const MIN_WAIT_MS = 2000;
  const SLOW_AFTER_MS = 9000;
  const STAGE_MS = 1400;

  const STEP_NAMES = ["La carte", "Tes mots", "Le message", "Ta carte"];

  /** `icon` : un symbole `cp-i-…` du sprite, dans le bloc Liquid. */
  const OCCASIONS = [
    {
      id: "anniversaire",
      label: "Anniversaire",
      hint: "Pour aller avec les bougies",
      icon: "cake",
    },
    {
      id: "naissance",
      label: "Naissance",
      hint: "Annonce, arrivée de bébé",
      icon: "baby",
    },
    {
      id: "amour",
      label: "Amour",
      hint: "Saint-Valentin, anniversaire de couple",
      icon: "hearts",
    },
    {
      id: "mariage",
      label: "Mariage",
      hint: "Fiançailles, témoins, mariés",
      icon: "rings",
    },
    {
      id: "amitie",
      label: "Amitié",
      hint: "Pour les copaines",
      icon: "cheers",
    },
    {
      id: "libre",
      label: "Sans occasion",
      hint: "Juste pour le plaisir",
      icon: "sparkles",
    },
  ];

  /**
   * Des questions pour trouver ses mots. Elles ne s'impriment pas : ce sont des
   * amorces. Une question à laquelle on a répondu ne revient plus. Écriture
   * neutre ou inclusive : la carte peut être pour n'importe qui. On tutoie
   * l'acheteur ; le « vous » qui reste désigne le couple ou la bande (amour,
   * amitié), pas l'acheteur.
   */
  const PROMPTS = {
    anniversaire: [
      "Son prénom, ou son surnom",
      "Son surnom d’enfance",
      "Son âge, écrit en lettres",
      "La ville de son enfance",
      "Sa ville d’aujourd’hui",
      "Son métier",
      "Sa passion",
      "Son sport",
      "Son plat préféré",
      "Son dessert d’anniversaire",
      "Son fromage préféré",
      "Sa boisson préférée",
      "Sa couleur préférée",
      "Sa fleur préférée",
      "Sa saison préférée",
      "Son signe astrologique",
      "Le prénom de son animal",
      "Le prénom de son acolyte",
      "Son groupe préféré",
      "Son jeu de société favori",
      "Sa destination de rêve",
      "Sa dernière destination de vacances",
      "Son petit plaisir du dimanche",
      "Le lieu de la fête",
    ],
    amour: [
      "Le petit nom que vous vous donnez",
      "La ville de votre rencontre",
      "Le mois de votre rencontre",
      "Le lieu de votre premier rendez-vous",
      "Le prénom de la personne qui vous a présenté·es",
      "La ville où vous vivez",
      "Votre destination de rêve",
      "Votre plus beau voyage",
      "Votre restaurant préféré",
      "Votre plat du dimanche",
      "Votre dessert à partager",
      "Votre boisson pour trinquer",
      "Votre série du moment",
      "Votre artiste préféré·e",
      "Votre jeu de société favori",
      "Votre sport à deux",
      "Votre saison préférée",
      "Le prénom de votre animal",
      "L’objet que vous gardez depuis le début",
      "Son plat signature",
      "Son parfum",
      "Son plus beau défaut",
      "Le mot qui vous résume",
    ],
    naissance: [
      "Le prénom du bébé",
      "Son deuxième prénom",
      "Le jour de sa naissance",
      "Le mois de sa naissance",
      "La saison de sa naissance",
      "Sa ville de naissance",
      "Le nom de la maternité",
      "Le prénom de la sage-femme",
      "Le prénom de l’un·e des parents",
      "Le prénom de l’un·e des grands-parents",
      "Le prénom de la marraine ou du parrain",
      "Le prénom du grand frère ou de la grande sœur",
      "Le prénom de l’animal de la maison",
      "Le surnom déjà trouvé",
      "Le nom du doudou",
      "La couleur de la chambre",
      "Le thème de la chambre",
      "L’animal de son mobile",
      "La couleur de ses yeux",
      "Son signe astrologique",
      "La ville où vit la famille",
      "Un mot doux pour l’accueillir",
    ],
    mariage: [
      "Le prénom de l’un·e des marié·es",
      "Le prénom de l’autre marié·e",
      "Le surnom du couple",
      "La ville de leur rencontre",
      "Le lieu de la demande",
      "Le lieu de la cérémonie",
      "Le mois du grand jour",
      "La ville où vit le couple",
      "Le prénom de l’un·e des témoins",
      "Le prénom de la personne qui prononce le discours",
      "La fleur star du bouquet",
      "La couleur phare de la déco",
      "Le plat du repas",
      "Le dessert du repas",
      "La boisson pour trinquer",
      "Le style de musique de la soirée",
      "La danse d’ouverture du bal",
      "La destination du voyage de noces",
      "Le prénom de leur animal",
      "Leur nombre d’années ensemble, en lettres",
      "Le mot qui résume ce jour",
    ],
    amitie: [
      "Son surnom",
      "Le lieu de votre rencontre",
      "La ville de vos vacances ensemble",
      "Votre destination de rêve",
      "Votre bar préféré",
      "Votre jeu de soirée préféré",
      "Le plat de vos soirées",
      "Votre cocktail signature",
      "La boisson des retrouvailles",
      "Sa série culte",
      "Votre prochain voyage ensemble",
      "Votre sport ensemble",
      "Le jour de vos retrouvailles",
      "Votre période préférée de l’année",
      "La ville de votre soirée la plus mémorable",
      "Son dessert préféré",
      "Sa chanson de karaoke préférée",
      "Ce qu'iel oublie à chaque fois",
      "Un mot que personne d’autre ne comprend",
    ],
    libre: [
      "Un prénom qui compte",
      "Une ville que tu aimes",
      "Un pays",
      "Ton plat préféré",
      "Un dessert",
      "Un fruit",
      "Une fleur",
      "Une couleur",
      "Un animal",
      "Une saison",
      "Un sport",
      "Un métier",
      "Un instrument de musique",
      "Un objet de la maison",
      "Un jeu",
      "Un moyen de transport",
      "Un souvenir, en un mot",
      "Un mot qui te fait sourire",
      "Un mot doux",
      "Une qualité",
      "Une odeur que tu aimes",
      "Un rêve, en un mot",
    ],
  };

  const MESSAGE_IDEAS = {
    anniversaire: ["Joyeux anniversaire", "Bon anniversaire", "Longue vie"],
    amour: ["Je t’aime", "Pour toujours", "Veux-tu m’épouser"],
    naissance: ["Bienvenue", "Félicitations", "Tu vas être mamie"],
    mariage: ["Vive l’amour", "Félicitations", "Veux-tu être mon témoin"],
    amitie: ["Merci pour tout", "BFF", "Je t'aime"],
    libre: ["Merci", "Bravo", "Je pense à toi"],
  };

  const TITLE_IDEAS = {
    anniversaire: "Joyeux anniversaire",
    amour: "Pour toi mon amour",
    naissance: "Bienvenue Sacha",
    mariage: "Pour Julie et Thomas",
    amitie: "Pour la bande",
    libre: "Un petit jeu pour toi",
  };

  const FORMAT_LABELS = {
    "card-a5": "Carte à offrir (A5)",
    "postcard-a6": "Carte postale (A6)",
  };
  const SHAPE_LABELS = { square: "carré", heart: "cœur" };

  /** La démonstration de l'étape 3 : quatre mots barrés, et MERCI dans les lettres qui restent. */
  const DEMO = {
    letters: "CHATMLOUPENUITRCETEI",
    words: [
      { word: "CHAT", cells: [0, 1, 2, 3] },
      { word: "LOUP", cells: [5, 6, 7, 8] },
      { word: "NUIT", cells: [10, 11, 12, 13] },
      { word: "ETE", cells: [16, 17, 18] },
    ],
    left: [4, 9, 14, 15, 19],
    answer: "MERCI",
  };

  const MESSAGES = {
    failed: "La fabrication n’a pas abouti. Réessaie dans un instant.",
    tooMany:
      "Tu as créé beaucoup de cartes en peu de temps. Réessaie dans une heure.",
  };

  /* ---- Outils ------------------------------------------------------------ */

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const plural = (n, one, many) => (n > 1 ? many : one);
  const esc = (text) =>
    String(text).replace(
      /[&<>"']/g,
      (c) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        })[c],
    );
  const icon = (name, extraClass = "") =>
    `<svg class="cp-icon${extraClass ? ` ${extraClass}` : ""}" aria-hidden="true"><use href="#cp-i-${name}"/></svg>`;
  const reducedMotion = () =>
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /** Capitales sans accent ni signe : la règle du serveur (`normalizeWord`). */
  const normalize = (text) =>
    text
      .replace(/\u0153/g, "oe")
      .replace(/\u0152/g, "OE")
      .replace(/\u00e6/g, "ae")
      .replace(/\u00c6/g, "AE")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^A-Za-z]/g, "")
      .toUpperCase();

  const boxes = (letters, numbered = false) =>
    `<span class="cp-boxes" aria-hidden="true">${[...letters]
      .map(
        (l, i) =>
          `<span class="cp-box">${numbered ? `<span class="cp-box__n">${i + 1}</span>` : ""}${l}</span>`,
      )
      .join("")}</span>`;

  const fresh = () => ({
    occasion: null,
    format: "card-a5",
    shape: "heart",
    words: [],
    hidden: "",
    title: "",
    signature: "",
    step: 1,
    promptIndex: 0,
    promptsDone: {},
    /** @type {{ id: string, token: string, previewPng: string, previewBackPng?: string, key: string, textKey: string } | null} */
    result: null,
    /**
     * La carte au panier : sa grille et sa livraison. Pas le titre ni la
     * signature, que le serveur réenregistre sur la même grille.
     * @type {{ id: string, variantId: number | null } | null}
     */
    inCart: null,
    refused: [],
    /** La livraison choisie à l'étape 4 (id de variante). */
    variantId: null,
  });

  class CustomPuzzleCreator extends HTMLElement {
    state = fresh();
    ui = {
      step: 1,
      making: false,
      makeToken: 0,
      face: 0,
      failure: null,
      confirmReset: false,
      demoTimer: 0,
      stageTimer: 0,
      textTimer: 0,
      rerender: null,
      /** Les mots tout juste ajoutés : seules leurs pastilles s'animent. */
      fresh: new Set(),
    };

    /**
     * Les livraisons, une par variante (bloc Liquid).
     * @type {{ id: number, title: string, price: string, available: boolean, soon: boolean }[]}
     */
    variants = [];

    /** @type {AbortController | null} */
    #abort = null;

    connectedCallback() {
      // Un envoi du formulaire produit avec une carte enregistrée, c'est une
      // carte au panier : la fiche le dit, et ne propose plus de la rajouter.
      // Écouté sur le document : Horizon remplace le formulaire quand la
      // variante change.
      this.#abort?.abort();
      this.#abort = new AbortController();
      const { signal } = this.#abort;
      document.addEventListener(
        "submit",
        (event) => {
          // `getAttribute` et non `.id` : le formulaire produit a un champ nommé
          // `id` (la variante), qui masque la propriété du même nom.
          const form = event.target;
          if (
            !(form instanceof HTMLFormElement) ||
            form.getAttribute("id") !== this.dataset.productForm
          )
            return;
          if (!this.#isUpToDate()) return;
          this.state.inCart = {
            id: this.state.result.id,
            variantId: this.#variant()?.id ?? null,
          };
          this.#save();
          queueMicrotask(() => {
            this.#writeProperties();
            this.#renderEntry();
          });
        },
        { capture: true, signal },
      );

      // Le panier fait foi : une carte retirée depuis le tiroir, ou dans un
      // autre onglet, ne doit plus s'afficher comme étant au panier.
      import("@shopify/events")
        .then(({ StandardEvents }) => {
          if (signal.aborted) return;
          document.addEventListener(
            StandardEvents.cartLinesUpdate,
            (event) =>
              event.promise?.then(
                () => this.#syncCart(),
                () => {},
              ),
            { signal },
          );
        })
        .catch(() => {});
      window.addEventListener(
        "pageshow",
        (event) => {
          if (event.persisted) this.#syncCart();
        },
        { signal },
      );

      if (this.studio) {
        this.#syncCart();
        return;
      }
      this.studio = this.querySelector("[data-studio]");
      this.viewer = this.querySelector("[data-viewer]");
      this.body = this.querySelector("[data-body]");
      this.entry = this.querySelector("[data-entry]");
      this.headingId = `cp-heading-${this.dataset.uid}`;
      try {
        this.variants = JSON.parse(
          this.querySelector("[data-variants]")?.textContent || "[]",
        );
      } catch {
        this.variants = [];
      }
      this.#load();

      this.entry.addEventListener("click", (event) =>
        this.#onEntryClick(event),
      );
      this.querySelector("[data-close]").addEventListener("click", () =>
        this.studio.close(),
      );
      this.querySelector("[data-back]").addEventListener("click", () => {
        if (this.ui.step > 1) this.#goTo(this.ui.step - 1);
      });
      this.querySelector("[data-primary]").addEventListener("click", () =>
        this.#onPrimary(),
      );
      this.studio.addEventListener("close", () => this.#onStudioClose());
      this.body.addEventListener("change", (event) =>
        this.#onStep1Change(event),
      );

      // Horizon s'arrête sur `form.checkValidity()` : le garde vide suffit à
      // bloquer l'ajout. Au lieu de la bulle native, on ouvre le studio.
      this.#property("id").addEventListener("invalid", (event) => {
        event.preventDefault();
        this.#openStudio();
      });

      this.viewer.addEventListener("click", (event) =>
        this.#onViewerClick(event),
      );

      this.#writeProperties();
      this.#renderEntry();
      this.#syncCart();
    }

    disconnectedCallback() {
      this.#abort?.abort();
    }

    /* ---- État ----------------------------------------------------------- */

    get #storageKey() {
      return `${this.dataset.storageKey}-v2`;
    }

    #load() {
      try {
        const saved = JSON.parse(
          localStorage.getItem(this.#storageKey) ?? "null",
        );
        if (saved) {
          this.state = { ...fresh(), ...saved };
          return;
        }
        // La première version gardait les mots dans un seul champ, un par
        // ligne : on les reprend. La grille, elle, repart de zéro.
        const old = JSON.parse(
          localStorage.getItem(this.dataset.storageKey) ?? "null",
        );
        if (old) {
          const words =
            typeof old.words === "string" ? old.words.split(/[\n,;]+/) : [];
          this.state = {
            ...fresh(),
            format: old.format === "postcard-a6" ? "postcard-a6" : "card-a5",
            words: [
              ...new Set(
                words
                  .map(normalize)
                  .filter(
                    (w) => w.length >= MIN_LENGTH && w.length <= MAX_LENGTH,
                  ),
              ),
            ],
            hidden: old.hidden ?? "",
            title: old.title ?? "",
            signature: old.signature ?? "",
          };
        }
      } catch {
        // Stockage illisible : on repart de zéro.
      }
    }

    #save() {
      try {
        localStorage.setItem(this.#storageKey, JSON.stringify(this.state));
      } catch {
        // Stockage indisponible (navigation privée, quota) : le parcours marche sans.
      }
    }

    #occasion() {
      return this.state.occasion || "libre";
    }

    #maxWords() {
      return MAX_WORDS[this.state.format];
    }

    #hiddenLetters() {
      return normalize(this.state.hidden);
    }

    /** Ce qui change la grille. */
    #gridKey() {
      const { format, shape, words } = this.state;
      return JSON.stringify([format, shape, words, this.#hiddenLetters()]);
    }

    /** Ce qui ne change que le texte imprimé (la carte postale n'en imprime pas). */
    #textKey() {
      if (this.state.format === "postcard-a6") return "";
      return JSON.stringify([
        this.state.title.trim(),
        this.state.signature.trim(),
      ]);
    }

    #hasCard() {
      return Boolean(
        this.state.result && this.state.result.key === this.#gridKey(),
      );
    }

    #isUpToDate() {
      return this.#hasCard() && this.state.result.textKey === this.#textKey();
    }

    /** La carte du studio est celle du panier : même grille, même livraison. */
    #isAdded() {
      const { inCart, result } = this.state;
      if (!inCart || !this.#hasCard() || inCart.id !== result.id) return false;
      const variant = this.#variant();
      return !variant || String(variant.id) === String(inCart.variantId);
    }

    /* ---- Formulaire produit ------------------------------------------- */

    #form() {
      return document.getElementById(this.dataset.productForm);
    }

    #property(name) {
      return this.querySelector(`[data-property="${name}"]`);
    }

    /** La livraison choisie, sinon la première disponible. */
    #variant() {
      const open = this.variants.filter((v) => v.available);
      return (
        open.find((v) => String(v.id) === String(this.state.variantId)) ??
        open[0] ??
        null
      );
    }

    /** Sans sélecteur sur la fiche, c'est le studio qui pose la variante. */
    #writeVariant() {
      const variant = this.#variant();
      const form = this.#form();
      if (!variant || !form) return;
      const input = /** @type {HTMLInputElement | null} */ (
        form.querySelector('input[name="id"]')
      );
      if (input) input.value = String(variant.id);
      // Arrivé par `?variant=` sur une livraison indisponible, Horizon a rendu
      // le bouton désactivé : il resterait muet au clic.
      for (const button of form.querySelectorAll('button[name="add"]')) {
        /** @type {HTMLButtonElement} */ (button).disabled = false;
      }
    }

    /** Carte à jour → propriétés remplies ; sinon, carte non enregistrée. */
    #writeProperties() {
      this.#writeVariant();
      const ready = this.#isUpToDate();
      const { result, words, format, shape } = this.state;
      const set = (name, value) => {
        const input = this.#property(name);
        input.value = value ?? "";
        // Un champ vide ne doit pas partir comme propriété de ligne.
        if (name !== "id") input.disabled = !value;
      };
      set("id", ready ? result.id : "");
      set("token", ready ? result.token : "");
      set("words", ready ? words.join(", ") : "");
      set("hidden", ready ? this.state.hidden.trim() : "");
      set(
        "format",
        ready ? `${FORMAT_LABELS[format]}, ${SHAPE_LABELS[shape]}` : "",
      );
      set(
        "link",
        ready
          ? `${this.dataset.downloadUrl}?id=${encodeURIComponent(result.id)}&token=${encodeURIComponent(result.token)}`
          : "",
      );
      // Les boutons d'achat d'Horizon n'apparaissent qu'avec une carte prête et
      // pas encore au panier. Au panier, c'est « Mettre à jour le panier » du
      // studio ou de la fiche qui remplace sa ligne.
      this.toggleAttribute("data-ready", ready && !this.state.inCart);
    }

    /* ---- Point d'entrée sur la fiche ---------------------------------- */

    #summary() {
      const { format, words } = this.state;
      return [
        FORMAT_LABELS[format].replace(/ \(.*\)/, ""),
        `${words.length} mots`,
        this.#hiddenLetters() ? "message caché" : "sans message",
        this.variants.length > 1 ? this.#variant()?.title : "",
      ]
        .filter(Boolean)
        .join(" · ");
    }

    /** La carte sur la fiche : un clic l'ouvre en grand, « Modifier » rouvre le studio. */
    #mine(title) {
      return `
        <div class="cp-mine">
          <span class="cp-mine__thumb">
            <img src="${esc(this.state.result.previewPng)}" alt="" width="76" height="76">
            <span class="cp-mine__zoom" aria-hidden="true">${icon("zoom")}</span>
          </span>
          <div class="cp-mine__text">
            <p class="cp-mine__title"><button class="cp-mine__view" type="button" data-act="view">${title}<span class="cp-sr-only">, voir ta carte en grand</span></button></p>
            <p class="cp-mine__meta">${esc(this.#summary())}</p>
          </div>
          <button class="filter cp-mine__edit" type="button" data-act="edit">Modifier</button>
        </div>`;
    }

    #renderEntry() {
      const entry = this.entry;
      if (this.ui.confirmReset) {
        // Une carte au panier y reste : seule la saisie de la fiche repart de zéro.
        const kept = Boolean(this.state.inCart);
        entry.innerHTML = `
          <div class="cp-confirm" role="group" aria-label="${kept ? "Créer une autre carte" : "Effacer la carte en cours"}">
            <p>${
              kept
                ? "<strong>Créer une autre carte ?</strong> Celle-ci reste dans ton panier, mais tu ne pourras plus la modifier."
                : "<strong>Effacer la carte en cours ?</strong> Tes mots et ton message seront perdus."
            }</p>
            <div class="cp-confirm__actions">
              <button class="btn-small glass glass--white cp-btn" type="button" data-act="reset">${kept ? "Nouvelle carte" : "Tout effacer"}</button>
              <button class="btn-small glass cp-btn" type="button" data-act="keep">${kept ? "Annuler" : "Garder ma carte"}</button>
            </div>
          </div>`;
        return;
      }

      const another =
        '<button class="cp-text-button" type="button" data-act="ask-reset">Créer une autre carte</button>';

      if (this.#isAdded()) {
        entry.innerHTML = `${this.#mine("Ta carte est dans le panier")}${another}`;
      } else if (this.#hasCard() && this.state.inCart) {
        entry.innerHTML = `
          ${this.#mine("Ta carte a changé")}
          <button class="btn-cart glass cp-block-button" type="button" data-act="update">Mettre à jour le panier</button>
          <p class="cp-entry__note">Ton panier a encore l’ancienne version.</p>
          ${another}`;
      } else if (this.#hasCard()) {
        entry.innerHTML = `${this.#mine(this.#isUpToDate() ? "Ta carte est prête" : "Mise à jour de ta carte…")}${another}`;
      } else if (this.state.words.length > 0 || this.state.occasion) {
        const n = this.state.words.length;
        const kept = this.state.inCart
          ? "L’ancienne version est toujours dans ton panier."
          : n > 1
            ? `Tes ${n} mots sont gardés.`
            : n === 1
              ? "Ton mot est gardé."
              : "Ta saisie est gardée.";
        entry.innerHTML = `
          <button class="btn-cart glass cp-block-button" type="button" data-act="open">Reprendre ma carte</button>
          <p class="cp-entry__note">${kept}
            <button class="cp-text-button" type="button" data-act="ask-reset">Recommencer</button></p>`;
      } else {
        entry.innerHTML = `
          <button class="btn-cart glass cp-block-button" type="button" data-act="open">Créer ma carte</button>
          <p class="cp-entry__note">Tu vois ta carte avant de payer.</p>`;
      }
    }

    #onEntryClick(event) {
      const button = event.target.closest("[data-act]");
      if (!button) return;
      const act = button.dataset.act;
      if (act === "open") this.#openStudio();
      if (act === "edit") this.#openStudio(4);
      if (act === "view") this.#openViewer();
      if (act === "update") {
        button.disabled = true;
        this.#addToCart();
      }
      if (act === "ask-reset") {
        this.ui.confirmReset = true;
        this.#renderEntry();
        this.entry.querySelector('[data-act="keep"]')?.focus();
      }
      if (act === "keep") {
        this.ui.confirmReset = false;
        this.#renderEntry();
      }
      if (act === "reset") {
        this.state = fresh();
        this.ui.confirmReset = false;
        this.#save();
        this.#writeProperties();
        this.#renderEntry();
      }
    }

    /* ---- La carte en grand -------------------------------------------- */

    #openViewer(face = 0) {
      const { result } = this.state;
      if (!result) return;
      const back = face === 1 && result.previewBackPng;
      const img = this.viewer.querySelector("[data-viewer-image]");
      img.src = back ? result.previewBackPng : result.previewPng;
      img.alt =
        this.state.format === "postcard-a6"
          ? `Aperçu de ta carte postale, ${back ? "verso" : "recto"}`
          : "Aperçu de ta carte";
      this.viewer.querySelector("[data-viewer-faces]").hidden =
        !result.previewBackPng;
      for (const button of this.viewer.querySelectorAll("[data-face]")) {
        button.setAttribute(
          "aria-pressed",
          String(Number(button.dataset.face) === (back ? 1 : 0)),
        );
      }
      if (!this.viewer.open) this.viewer.showModal();
    }

    #onViewerClick(event) {
      // Un clic hors de la fenêtre tombe sur le `<dialog>` lui-même (le fond).
      if (event.target === this.viewer) this.viewer.close();
      const target = event.target.closest("button");
      if (!target) return;
      if (target.matches("[data-viewer-close]")) this.viewer.close();
      if (target.matches("[data-face]"))
        this.#openViewer(Number(target.dataset.face));
      if (target.matches("[data-viewer-edit]")) {
        this.viewer.close();
        this.#openStudio(4);
      }
    }

    /* ---- Studio : ouverture, étapes, pied ----------------------------- */

    #openStudio(step) {
      this.ui.step =
        step || (this.#hasCard() ? 4 : Math.min(this.state.step || 1, 3));
      document.documentElement.classList.add("cp-locked");
      if (!this.studio.open) this.studio.showModal();
      this.#renderStep();
    }

    #onStudioClose() {
      document.documentElement.classList.remove("cp-locked");
      this.#stopDemo();
      clearInterval(this.ui.stageTimer);
      this.state.step = Math.min(this.ui.step, 3);
      this.#save();
      // Un titre modifié sans nouvel aperçu : on le fabrique, pour que la carte
      // enregistrée porte bien ce que l'acheteur a tapé.
      if (this.#hasCard() && !this.#isUpToDate()) this.#rerender();
      this.#writeProperties();
      this.#renderEntry();
    }

    #goTo(step) {
      this.ui.step = step;
      this.state.step = Math.min(step, 3);
      this.#save();
      this.#renderStep();
    }

    #renderStep() {
      this.#stopDemo();
      const step = this.ui.step;
      this.querySelector("[data-step-label]").textContent =
        `Étape ${step} sur 4 · ${STEP_NAMES[step - 1]}`;
      this.querySelector("[data-progress]").innerHTML = STEP_NAMES.map(
        (name, i) => {
          const n = i + 1;
          const cls = n < step ? "is-done" : n === step ? "is-current" : "";
          return `<li class="${cls}"${n === step ? ' aria-current="step"' : ""}><span class="cp-progress__bar"></span><span class="cp-progress__label">${name}</span></li>`;
        },
      ).join("");
      this.querySelector(".cp-foot").classList.toggle("is-wide", step === 4);

      this.body.innerHTML = [
        this.#step1,
        this.#step2,
        this.#step3,
        this.#step4,
      ][step - 1].call(this);
      this.body.scrollTop = 0;
      [this.#bind1, this.#bind2, this.#bind3, this.#bind4][step - 1].call(this);
      this.#updateFoot();
      this.querySelector(`#${this.headingId}`)?.focus({ preventScroll: true });
    }

    #foot() {
      const n = this.state.words.length;
      const step = this.ui.step;
      if (step === 1) {
        return this.state.occasion
          ? { label: "Continuer", disabled: false }
          : { label: "Choisis une occasion", disabled: true };
      }
      if (step === 2) {
        if (n < MIN_WORDS) {
          const missing = MIN_WORDS - n;
          return {
            label: `Encore ${missing} ${plural(missing, "mot", "mots")}`,
            disabled: true,
          };
        }
        if (n > this.#maxWords())
          return { label: "Trop de mots", disabled: true };
        if (this.#refusedIn(this.state.words).length)
          return { label: "Retire les mots refusés", disabled: true };
        return { label: "Continuer", disabled: false };
      }
      if (step === 3) {
        if (this.#hiddenProblem())
          return { label: "Créer ma carte", disabled: true };
        return {
          label: this.#hiddenLetters()
            ? "Créer ma carte"
            : "Créer ma carte sans message",
          disabled: false,
        };
      }
      return {
        label: this.state.inCart
          ? "Mettre à jour le panier"
          : "Ajouter au panier",
        disabled: this.ui.making || !this.#hasCard(),
      };
    }

    #updateFoot() {
      const { label, disabled } = this.#foot();
      const primary = this.querySelector("[data-primary]");
      primary.textContent = label;
      primary.disabled = disabled;
      this.querySelector("[data-back]").hidden = this.ui.step === 1;
    }

    #onPrimary() {
      if (this.#foot().disabled) return;
      if (this.ui.step < 4) this.#goTo(this.ui.step + 1);
      else this.#addToCart();
    }

    /* ---- Étape 1 : la carte ------------------------------------------- */

    #step1() {
      const { occasion, format, shape } = this.state;
      const check = `<span class="cp-tile__check">${icon("check")}</span>`;
      return `
        <div class="cp-step">
          <h2 class="cp-title" id="${this.headingId}" tabindex="-1">C’est pour quelle occasion ?</h2>
          <p class="cp-lead">On te proposera des idées de mots et de messages qui vont avec.</p>
          <div class="cp-tiles cp-tiles--occasion" role="radiogroup" aria-labelledby="${this.headingId}">
            ${OCCASIONS.map(
              (o) => `
              <label class="cp-tile cp-tile--occasion">
                <input type="radio" name="cp-occasion" value="${o.id}" ${occasion === o.id ? "checked" : ""}>
                ${icon(o.icon, "cp-tile__icon")}
                <span class="cp-tile__label">${o.label}</span>
                <span class="cp-tile__hint">${o.hint}</span>
                ${check}
              </label>`,
            ).join("")}
          </div>

          <h3 class="cp-subtitle" id="${this.headingId}-format">Quel format ?</h3>
          <div class="cp-tiles cp-tiles--format" role="radiogroup" aria-labelledby="${this.headingId}-format">
            <label class="cp-tile cp-tile--format">
              <input type="radio" name="cp-format" value="card-a5" ${format === "card-a5" ? "checked" : ""}>
              <span class="cp-format-art cp-format-art--a5" aria-hidden="true"></span>
              <span class="cp-tile__label">Carte à offrir</span>
              <span class="cp-tile__hint">A5 · 14,8 × 21 cm<br>Jusqu’à ${MAX_WORDS["card-a5"]} mots</span>
              ${check}
            </label>
            <label class="cp-tile cp-tile--format">
              <input type="radio" name="cp-format" value="postcard-a6" ${format === "postcard-a6" ? "checked" : ""}>
              <span class="cp-format-art cp-format-art--a6" aria-hidden="true"></span>
              <span class="cp-tile__label">Carte postale</span>
              <span class="cp-tile__hint">A6 · 14,8 × 10,5 cm, recto verso<br>Jusqu’à ${MAX_WORDS["postcard-a6"]} mots</span>
              ${check}
            </label>
          </div>
          <p class="cp-warn" data-format-warn hidden></p>

          <h3 class="cp-subtitle" id="${this.headingId}-shape">Quelle forme de grille ?</h3>
          <div class="cp-segments" role="radiogroup" aria-labelledby="${this.headingId}-shape">
            <label class="cp-segment">
              <input type="radio" name="cp-shape" value="heart" ${shape === "heart" ? "checked" : ""}>
              ${icon("heart")}Cœur
            </label>
            <label class="cp-segment">
              <input type="radio" name="cp-shape" value="square" ${shape === "square" ? "checked" : ""}>
              ${icon("square")}Carré
            </label>
          </div>
        </div>`;
    }

    #bind1() {
      this.#updateFormatWarn();
    }

    #updateFormatWarn() {
      const warn = this.body.querySelector("[data-format-warn]");
      if (!warn) return;
      const extra = this.state.words.length - this.#maxWords();
      warn.hidden = extra <= 0;
      warn.textContent =
        extra > 0
          ? `Tu as ${this.state.words.length} mots : la carte postale en tient ${this.#maxWords()}. Il faudra en retirer ${extra}.`
          : "";
    }

    #onStep1Change(event) {
      const input = event.target;
      if (this.ui.step !== 1 || input.type !== "radio") return;
      if (input.name === "cp-occasion" && this.state.occasion !== input.value) {
        this.state.occasion = input.value;
        this.state.promptIndex = -1;
      }
      if (input.name === "cp-format") this.state.format = input.value;
      if (input.name === "cp-shape") this.state.shape = input.value;
      this.#save();
      this.#writeProperties();
      this.#updateFormatWarn();
      this.#updateFoot();
    }

    /* ---- Étape 2 : les mots ------------------------------------------ */

    #step2() {
      return `
        <div class="cp-step">
          <h2 class="cp-title" id="${this.headingId}" tabindex="-1">Tes mots</h2>
          <p class="cp-lead">Prénoms, lieux, surnoms, souvenirs : les mots qui n’appartiennent qu’à toi.</p>

          <div class="cp-idea">
            <p class="cp-idea__label">Une idée ?</p>
            <p class="cp-idea__text" data-idea></p>
            <button class="cp-text-button" type="button" data-idea-next>${icon("refresh")}Autre idée</button>
          </div>

          <form class="cp-add" data-add novalidate>
            <label class="cp-sr-only" for="${this.headingId}-word">Ajouter un mot</label>
            <input class="field cp-field cp-add__input" id="${this.headingId}-word" type="text" autocomplete="off"
              autocapitalize="characters" spellcheck="false" enterkeyhint="done" maxlength="200" placeholder="Tape un mot">
            <button class="btn-small glass cp-btn cp-add__button" type="submit" data-add-button>${icon("plus")}Ajouter</button>
          </form>
          <div class="cp-typing" data-typing aria-live="polite"></div>

          <ul class="cp-chips" data-chips aria-label="Tes mots"></ul>
          <p class="cp-counter" data-counter aria-live="polite"></p>
        </div>`;
    }

    #wordInput() {
      return this.body.querySelector(`#${this.headingId}-word`);
    }

    #wordProblem(word) {
      if (word.length < MIN_LENGTH)
        return `« ${word} » est un peu court : ${MIN_LENGTH} lettres au minimum.`;
      if (word.length > MAX_LENGTH)
        return `« ${word} » ne rentre pas : ${MAX_LENGTH} lettres au maximum.`;
      if (this.state.words.includes(word))
        return `« ${word} » est déjà dans la liste.`;
      if (this.state.words.length >= this.#maxWords())
        return `C’est complet : ${this.#maxWords()} mots au maximum.`;
      return null;
    }

    #promptsDone() {
      const key = this.#occasion();
      if (!Array.isArray(this.state.promptsDone[key]))
        this.state.promptsDone[key] = [];
      return this.state.promptsDone[key];
    }

    /** Prochaine question sans réponse après `from`, ou -1 quand tout y est passé. */
    #nextPrompt(from) {
      const list = PROMPTS[this.#occasion()];
      const done = new Set(this.#promptsDone());
      for (let k = 1; k <= list.length; k++) {
        const i = (((from + k) % list.length) + list.length) % list.length;
        if (!done.has(i)) return i;
      }
      return -1;
    }

    #currentPrompt() {
      if (this.state.promptIndex >= PROMPTS[this.#occasion()].length)
        this.state.promptIndex = -1;
      if (
        this.state.promptIndex < 0 ||
        this.#promptsDone().includes(this.state.promptIndex)
      ) {
        this.state.promptIndex = this.#nextPrompt(
          this.state.promptIndex < 0 ? -1 : this.state.promptIndex,
        );
      }
      return this.state.promptIndex;
    }

    /** Un mot vient d'être posé : la question affichée a sa réponse, on passe à la suivante. */
    #answerPrompt() {
      const i = this.#currentPrompt();
      if (i < 0) return;
      this.#promptsDone().push(i);
      this.state.promptIndex = this.#nextPrompt(i);
    }

    #renderIdea(animate = false) {
      const text = this.body.querySelector("[data-idea]");
      const i = this.#currentPrompt();
      this.body.querySelector("[data-idea-next]").hidden = i < 0;
      text.textContent =
        i < 0
          ? "Tu as fait le tour de nos idées. À toi de jouer !"
          : PROMPTS[this.#occasion()][i];
      if (animate && !reducedMotion()) {
        text.classList.remove("is-new");
        void text.offsetWidth;
        text.classList.add("is-new");
      }
    }

    #renderChips() {
      const refused = new Set(this.state.refused);
      this.body.querySelector("[data-chips]").innerHTML = this.state.words
        .length
        ? this.state.words
            .map(
              (w) =>
                `<li class="cp-chip${refused.has(w) ? " is-refused" : ""}${this.ui.fresh.has(w) ? " is-new" : ""}">${w}<button class="cp-chip__remove" type="button" data-remove="${w}" aria-label="Retirer ${w}">${icon("close")}</button></li>`,
            )
            .join("")
        : '<li class="cp-chips__empty">Tes mots apparaîtront ici.</li>';
      this.ui.fresh.clear();
    }

    #renderCounter() {
      const n = this.state.words.length;
      const max = this.#maxWords();
      const refused = this.#refusedIn(this.state.words);
      let text;
      if (refused.length)
        text = `Ces mots ne sont pas acceptés : ${refused.join(", ")}. Retire-les pour continuer.`;
      else if (n > max)
        text = `La carte postale tient ${max} mots : retires-en ${n - max}.`;
      else if (n === max) {
        text =
          this.state.format === "postcard-a6"
            ? `${max} mots : la carte postale est complète.`
            : `${max} mots : c’est complet.`;
      } else if (n >= MIN_WORDS)
        text = `${n} mots. Tu peux continuer, ou en ajouter jusqu’à ${max}.`;
      else if (n === 0) text = `Ajoute au moins ${MIN_WORDS} mots.`;
      else
        text = `Encore ${MIN_WORDS - n} ${plural(MIN_WORDS - n, "mot", "mots")} pour continuer.`;
      const counter = this.body.querySelector("[data-counter]");
      counter.textContent = text;
      counter.classList.toggle("is-problem", n > max || refused.length > 0);
      const input = this.#wordInput();
      input.disabled = n >= max;
      input.placeholder = n >= max ? "C’est complet" : "Tape un mot";
    }

    /** Ce que vaut le mot en cours de frappe : ajoutable, trop court, ou refusé. */
    #typedWord() {
      const word = normalize(this.#wordInput().value);
      if (!word) return { word, ok: false };
      const problem = this.#wordProblem(word);
      if (problem && word.length >= MIN_LENGTH)
        return { word, ok: false, problem };
      if (word.length < MIN_LENGTH) {
        return {
          word,
          ok: false,
          note: `${word.length} ${plural(word.length, "lettre", "lettres")} · ${MIN_LENGTH} au minimum`,
        };
      }
      return {
        word,
        ok: true,
        note: `${word.length} lettres · Entrée pour l’ajouter`,
      };
    }

    #renderTyping(message) {
      const typing = this.body.querySelector("[data-typing]");
      const typed = this.#typedWord();
      // « Ajouter » ne s'allume que pour un mot qui peut vraiment entrer.
      this.body.querySelector("[data-add-button]").disabled = !typed.ok;
      if (message) {
        typing.innerHTML = `<p class="cp-problem">${esc(message)}</p>`;
        return;
      }
      if (!typed.word) {
        typing.innerHTML = "";
        return;
      }
      typing.innerHTML = typed.problem
        ? `${boxes(typed.word)}<p class="cp-problem">${esc(typed.problem)}</p>`
        : `${boxes(typed.word)}<span class="cp-typing__meta">${typed.note}</span>`;
    }

    #refreshWords(message) {
      this.#renderChips();
      this.#renderCounter();
      this.#renderTyping(message);
      this.#updateFoot();
      this.#writeProperties();
    }

    #addFromInput() {
      const input = this.#wordInput();
      const parts = input.value
        .split(/[\n,;]+/)
        .map((part) => part.trim())
        .filter(Boolean);
      if (parts.length === 0) return;
      const refused = [];
      let added = 0;
      for (const part of parts) {
        const word = normalize(part);
        if (!word) continue;
        const problem = this.#wordProblem(word);
        if (problem) refused.push(problem);
        else {
          this.state.words.push(word);
          this.ui.fresh.add(word);
          added += 1;
        }
      }
      input.value = "";
      if (added > 0) {
        this.#answerPrompt();
        this.#renderIdea(true);
      }
      this.#save();
      this.#refreshWords(
        refused.length === 1
          ? refused[0]
          : refused.length > 1
            ? `${refused.length} mots n’ont pas été ajoutés. ${refused[0]}`
            : undefined,
      );
      if (!input.disabled) input.focus();
    }

    #bind2() {
      this.#renderIdea();
      this.#refreshWords();

      this.body
        .querySelector("[data-idea-next]")
        .addEventListener("click", () => {
          this.state.promptIndex = this.#nextPrompt(this.#currentPrompt());
          this.#save();
          this.#renderIdea(true);
        });
      this.body
        .querySelector("[data-add]")
        .addEventListener("submit", (event) => {
          event.preventDefault();
          this.#addFromInput();
        });
      this.#wordInput().addEventListener("input", (event) => {
        // Une liste collée part d'un coup ; un mot tapé attend Entrée.
        if (/[\n,;]/.test(event.target.value)) this.#addFromInput();
        else this.#renderTyping();
      });
      this.body
        .querySelector("[data-chips]")
        .addEventListener("click", (event) => {
          const button = event.target.closest("[data-remove]");
          if (!button) return;
          this.state.words = this.state.words.filter(
            (w) => w !== button.dataset.remove,
          );
          this.#save();
          this.#refreshWords();
          this.#wordInput().focus();
        });
      if (window.matchMedia("(min-width: 750px)").matches)
        this.#wordInput().focus();
    }

    /* ---- Étape 3 : le message --------------------------------------- */

    #step3() {
      const ideas = MESSAGE_IDEAS[this.#occasion()];
      return `
        <div class="cp-step">
          <h2 class="cp-title" id="${this.headingId}" tabindex="-1">Un message caché ?</h2>
          <p class="cp-lead">Quand tous les mots de la liste sont barrés, les lettres qui restent, lues ligne par ligne, forment ton message. C’est facultatif.</p>

          <div class="cp-demo" aria-hidden="true">
            <div class="cp-demo__grid" data-demo-grid>
              ${[...DEMO.letters].map((l) => `<span class="cp-demo__cell">${l}</span>`).join("")}
            </div>
            <div class="cp-demo__side">
              <p class="cp-demo__words" data-demo-words>${DEMO.words.map((w) => `<span>${w.word}</span>`).join(" ")}</p>
              <p class="cp-demo__answer" data-demo-answer></p>
              <p class="cp-demo__caption">Exemple : quatre mots barrés, et le message se lit.</p>
            </div>
          </div>

          <label class="cp-label" for="${this.headingId}-hidden">Ton message</label>
          <input class="field cp-field" id="${this.headingId}-hidden" type="text" maxlength="${MAX_TEXT}" autocomplete="off"
            spellcheck="false" placeholder="Tape ton message" value="${esc(this.state.hidden)}">
          <div class="cp-typing" data-hidden-typing aria-live="polite"></div>
          <p class="cp-fine">Les accents, les espaces et les apostrophes disparaissent : « Je t’aime » devient JETAIME.</p>

          <p class="cp-label">Des idées</p>
          <div class="cp-suggestions" data-suggestions>
            ${ideas.map((m) => `<button class="filter cp-suggestion" type="button" data-idea="${esc(m)}">${esc(m)}</button>`).join("")}
          </div>
        </div>`;
    }

    /** Les mots de `list` que la modération a refusés au dernier essai. */
    #refusedIn(list) {
      return this.state.refused.filter((w) => list.includes(w));
    }

    /** Les mots du message, tels que la modération les découpe (`textTokens`). */
    #hiddenTokens() {
      return this.state.hidden
        .split(/[^\p{L}]+/u)
        .map(normalize)
        .filter(Boolean);
    }

    #hiddenProblem() {
      const letters = this.#hiddenLetters();
      if (!letters) return null;
      const refused = this.#refusedIn(this.#hiddenTokens());
      if (refused.length)
        return `« ${refused.join(", ")} » n’est pas accepté sur nos cartes.`;
      if (letters.length < MIN_HIDDEN)
        return `${MIN_HIDDEN} lettres au minimum.`;
      if (letters.length > MAX_HIDDEN)
        return `${MAX_HIDDEN} lettres au maximum.`;
      if (this.state.words.includes(letters))
        return `« ${letters} » est déjà dans tes mots : ce ne serait plus une surprise.`;
      return null;
    }

    #renderHiddenTyping() {
      const letters = this.#hiddenLetters();
      const problem = this.#hiddenProblem();
      const typing = this.body.querySelector("[data-hidden-typing]");
      typing.innerHTML = letters
        ? `${boxes(letters, true)}${problem ? `<p class="cp-problem">${esc(problem)}</p>` : `<span class="cp-typing__meta">${letters.length} lettres</span>`}`
        : "";
    }

    #demoPhase(phase) {
      const cells = this.body.querySelectorAll(".cp-demo__cell");
      const words = this.body.querySelectorAll("[data-demo-words] span");
      if (!cells.length) return;
      const crossed = new Set(
        DEMO.words
          .slice(0, Math.min(phase, DEMO.words.length))
          .flatMap((w) => w.cells),
      );
      const reveal = phase > DEMO.words.length;
      cells.forEach((cell, i) => {
        cell.classList.toggle("is-crossed", crossed.has(i));
        cell.classList.toggle("is-left", reveal && DEMO.left.includes(i));
      });
      words.forEach((w, i) => w.classList.toggle("is-crossed", i < phase));
      this.body.querySelector("[data-demo-answer]").textContent = reveal
        ? DEMO.answer
        : "";
    }

    #stopDemo() {
      clearInterval(this.ui.demoTimer);
      this.ui.demoTimer = 0;
    }

    #startDemo() {
      this.#stopDemo();
      const last = DEMO.words.length + 1;
      if (reducedMotion()) {
        this.#demoPhase(last);
        return;
      }
      let phase = 0;
      this.#demoPhase(phase);
      this.ui.demoTimer = setInterval(() => {
        phase = phase >= last + 2 ? 0 : phase + 1;
        this.#demoPhase(Math.min(phase, last));
      }, 900);
    }

    #bind3() {
      this.#startDemo();
      this.#renderHiddenTyping();
      const input = this.body.querySelector(`#${this.headingId}-hidden`);
      const update = () => {
        this.state.hidden = input.value;
        this.#save();
        this.#writeProperties();
        this.#renderHiddenTyping();
        this.#updateFoot();
      };
      input.addEventListener("input", update);
      this.body
        .querySelector("[data-suggestions]")
        .addEventListener("click", (event) => {
          const button = event.target.closest("[data-idea]");
          if (!button) return;
          input.value = button.dataset.idea;
          update();
        });
    }

    /* ---- Étape 4 : la carte ----------------------------------------- */

    #step4() {
      const a6 = this.state.format === "postcard-a6";
      const letters = this.#hiddenLetters();
      return `
        <div class="cp-step cp-step--wide">
          <div class="cp-making" data-making role="status" hidden>
            <div class="cp-making__grid" data-making-grid aria-hidden="true"></div>
            <ol class="cp-making__steps" data-making-steps></ol>
            <p class="cp-making__slow" data-making-slow hidden></p>
          </div>

          <div class="cp-failure" data-failure hidden></div>

          <div class="cp-result" data-result hidden>
            <h2 class="cp-title cp-result__title" id="${this.headingId}" tabindex="-1">Ta carte</h2>
            <div class="cp-result__card">
              <div class="cp-sheet" data-sheet><img data-preview alt="Aperçu de ta carte"></div>
              ${
                a6
                  ? `<div class="cp-faces" data-faces>
                      <button class="filter cp-face" type="button" data-face="0" aria-pressed="true">Recto</button>
                      <button class="filter cp-face" type="button" data-face="1" aria-pressed="false">Verso</button>
                    </div>`
                  : ""
              }
            </div>
            <div class="cp-actions cp-result__actions">
              <button class="filter cp-filter" type="button" data-another>${icon("refresh")}Une autre grille</button>
              <button class="cp-text-button" type="button" data-edit-words>Modifier mes mots</button>
            </div>
            ${
              a6
                ? '<p class="cp-fine cp-result__texts">Au verso : la place pour ton message écrit à la main, l’adresse et le timbre.</p>'
                : `<div class="cp-texts cp-result__texts">
                    <div class="cp-text">
                      <label for="${this.headingId}-title">Titre, en haut de la carte</label>
                      <input class="field cp-field" id="${this.headingId}-title" type="text" maxlength="${MAX_TEXT}" autocomplete="off"
                        placeholder="Ex. : ${esc(TITLE_IDEAS[this.#occasion()])}" value="${esc(this.state.title)}" data-text="title">
                    </div>
                    <div class="cp-text">
                      <label for="${this.headingId}-signature">Signature, en bas</label>
                      <input class="field cp-field" id="${this.headingId}-signature" type="text" maxlength="${MAX_TEXT}" autocomplete="off"
                        placeholder="Ex. : Marie, Paul et les enfants" value="${esc(this.state.signature)}" data-text="signature">
                    </div>
                    <p class="cp-fine">Facultatifs. Sans titre, la carte porte le logo OQP.</p>
                  </div>`
            }
            <ul class="cp-facts cp-result__facts">
              <li>${icon("check")}<span>Tes mots sont soulignés. Les autres complètent la grille.</span></li>
              ${
                letters
                  ? `<li>${icon("check")}<span>Ton message se cache dans les lettres qui restent : <strong>${esc(this.state.hidden.trim())}</strong>.</span></li>`
                  : ""
              }
            </ul>
            ${this.#deliveryChoice()}
            <ul class="cp-reassure cp-result__reassure">
              <li>${icon("check")}<span>Tu recevras exactement cette carte, sans la mention « Aperçu ».</span></li>
              <li>${icon("check")}<span>PDF prêt à imprimer, disponible dès le paiement.</span></li>
            </ul>
          </div>
        </div>`;
    }

    /** Une fois la carte vue, on dit comment on la veut. Une livraison à venir reste en vue, grisée. */
    #deliveryChoice() {
      if (this.variants.length < 2) return "";
      const chosen = this.#variant();
      const id = `${this.headingId}-delivery`;
      return `
        <div class="cp-delivery cp-result__delivery">
          <h3 class="cp-subtitle" id="${id}">Tu la reçois comment ?</h3>
          <div class="cp-tiles" role="radiogroup" aria-labelledby="${id}" data-delivery>
            ${this.variants
              .map((v) => {
                const status = v.available
                  ? ""
                  : v.soon
                    ? "Bientôt"
                    : "Indisponible";
                return `
              <label class="cp-tile">
                <input type="radio" name="cp-delivery" value="${v.id}" ${chosen?.id === v.id ? "checked" : ""} ${v.available ? "" : "disabled"}>
                <span class="cp-tile__label">${esc(v.title)}</span>
                <span class="cp-tile__hint">${esc(v.price)}${status ? `<span class="cp-tile__soon">${status}</span>` : ""}</span>
                <span class="cp-tile__check">${icon("check")}</span>
              </label>`;
              })
              .join("")}
          </div>
        </div>`;
    }

    #show(part) {
      for (const name of ["making", "failure", "result"]) {
        const el = this.body.querySelector(`[data-${name}]`);
        if (el) el.hidden = name !== part;
      }
    }

    #bind4() {
      this.body
        .querySelector("[data-another]")
        .addEventListener("click", () => {
          if (!this.ui.making) this.#make();
        });
      this.body
        .querySelector("[data-edit-words]")
        .addEventListener("click", () => this.#goTo(2));
      this.body
        .querySelector("[data-faces]")
        ?.addEventListener("click", (event) => {
          const button = event.target.closest("[data-face]");
          if (!button) return;
          this.ui.face = Number(button.dataset.face);
          this.#showPreview();
        });
      this.body
        .querySelector("[data-delivery]")
        ?.addEventListener("change", (event) => {
          this.state.variantId = event.target.value;
          this.#save();
          this.#writeVariant();
        });
      for (const input of this.body.querySelectorAll("[data-text]")) {
        input.addEventListener("input", () => {
          this.state[input.dataset.text] = input.value;
          this.#save();
          this.#writeProperties();
          this.body.querySelector("[data-sheet]")?.classList.add("is-stale");
          this.#updateFoot();
          // Titre et signature ne touchent pas la grille : nouvel aperçu, même carte.
          clearTimeout(this.ui.textTimer);
          this.ui.textTimer = setTimeout(() => this.#rerender(), 700);
        });
      }

      this.ui.face = 0;
      if (this.#hasCard()) {
        this.#show("result");
        this.#showPreview();
        if (!this.#isUpToDate()) this.#rerender();
      } else {
        this.#make();
      }
    }

    #showPreview() {
      const img = this.body.querySelector("[data-preview]");
      if (!img || !this.state.result) return;
      const { previewPng, previewBackPng } = this.state.result;
      const back = this.ui.face === 1 && previewBackPng;
      img.src = back ? previewBackPng : previewPng;
      img.alt =
        this.state.format === "postcard-a6"
          ? `Aperçu de ta carte postale, ${back ? "verso" : "recto"}`
          : "Aperçu de ta carte";
      for (const button of this.body.querySelectorAll("[data-face]")) {
        button.setAttribute(
          "aria-pressed",
          String(Number(button.dataset.face) === (back ? 1 : 0)),
        );
      }
      this.body
        .querySelector("[data-sheet]")
        ?.classList.toggle("is-stale", !this.#isUpToDate());
    }

    /* ---- Fabrication -------------------------------------------------- */

    async #post(body) {
      const response = await fetch(
        `${this.dataset.apiBase}/api-custom-puzzle-generate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": this.dataset.apiKey,
          },
          body: JSON.stringify(body),
        },
      );
      const data = await response.json().catch(() => ({}));
      return { status: response.status, ok: response.ok, data };
    }

    #textFields() {
      if (this.state.format === "postcard-a6") return {};
      return {
        title: this.state.title.trim() || undefined,
        signature: this.state.signature.trim() || undefined,
      };
    }

    #setStage(current) {
      const list = this.body.querySelector("[data-making-steps]");
      if (!list) return;
      const stages = [
        "On pose tes mots dans la grille",
        "On croise les lettres",
        this.#hiddenLetters()
          ? "On cache ton message"
          : "On complète la grille",
        "Dernières vérifications",
      ];
      list.innerHTML = stages
        .map((label, i) => {
          const cls =
            i < current ? "is-done" : i === current ? "is-current" : "";
          return `<li class="cp-making__step ${cls}"><span class="cp-making__dot">${icon("check")}</span>${label}…</li>`;
        })
        .join("");
    }

    async #make() {
      const token = ++this.ui.makeToken;
      const withMessage = Boolean(this.#hiddenLetters());
      const key = this.#gridKey();
      const textKey = this.#textKey();
      this.ui.making = true;
      this.ui.face = 0;
      this.#show("making");
      const letters = (
        this.state.words.join("") + this.#hiddenLetters() || "OQPMOTSMELES"
      ).split("");
      this.body.querySelector("[data-making-grid]").innerHTML = Array.from(
        { length: 36 },
        (_, i) => {
          const delay = ((i % 6) + Math.floor(i / 6)) * 90;
          return `<span class="cp-making__cell" style="animation-delay:${delay}ms">${letters[(i * 7) % letters.length]}</span>`;
        },
      ).join("");
      this.#setStage(0);
      this.#updateFoot();

      const started = Date.now();
      let stage = 0;
      clearInterval(this.ui.stageTimer);
      this.ui.stageTimer = setInterval(() => {
        if (token !== this.ui.makeToken || this.ui.step !== 4) return;
        stage = Math.min(stage + 1, 3);
        this.#setStage(stage);
        const slow = this.body.querySelector("[data-making-slow]");
        if (slow && Date.now() - started > SLOW_AFTER_MS) {
          slow.hidden = false;
          slow.textContent = withMessage
            ? "Ton message demande un peu plus de travail : on essaie une autre grille."
            : "Tes mots demandent un peu plus de travail : on essaie une autre grille.";
        }
      }, STAGE_MS);

      let reply;
      try {
        [reply] = await Promise.all([
          this.#post({
            format: this.state.format,
            shape: this.state.shape,
            words: this.state.words,
            hidden: this.state.hidden.trim() || undefined,
            // Oriente les mots de remplissage du message caché.
            occasion: this.#occasion(),
            ...this.#textFields(),
          }),
          wait(MIN_WAIT_MS),
        ]);
      } catch {
        reply = { status: 0, ok: false, data: {} };
      }
      clearInterval(this.ui.stageTimer);
      if (token !== this.ui.makeToken) return;
      this.ui.making = false;

      if (reply.ok) {
        const {
          id,
          token: puzzleToken,
          previewPng,
          previewBackPng,
        } = reply.data;
        this.state.result = {
          id,
          token: puzzleToken,
          previewPng,
          previewBackPng,
          key,
          textKey,
        };
        this.state.refused = [];
        this.#save();
        this.#writeProperties();
        if (this.ui.step === 4 && this.studio.open) {
          this.#setStage(4);
          this.#show("result");
          this.#showPreview();
          this.querySelector(`#${this.headingId}`)?.focus({
            preventScroll: true,
          });
          // Titre ou signature tapés pendant la fabrication : on les applique.
          if (!this.#isUpToDate()) this.#rerender();
        } else {
          this.#renderEntry();
        }
      } else if (this.ui.step === 4 && this.studio.open) {
        this.#showFailure(reply, withMessage);
      }
      this.#updateFoot();
    }

    /** Titre ou signature modifiés : nouvel aperçu de la même grille, sans moteur. */
    async #rerender() {
      if (!this.#hasCard() || this.#isUpToDate()) return;
      if (this.ui.rerender) return this.ui.rerender;
      const textKey = this.#textKey();
      const run = async () => {
        const { id, token } = this.state.result;
        let reply;
        try {
          reply = await this.#post({ id, token, ...this.#textFields() });
        } catch {
          reply = { status: 0, ok: false, data: {} };
        }
        if (!this.#hasCard() || this.state.result.id !== id) return;
        if (reply.ok) {
          this.state.result = {
            ...this.state.result,
            previewPng: reply.data.previewPng,
            previewBackPng: reply.data.previewBackPng,
            textKey,
          };
          this.#save();
        } else if (reply.status === 404 || reply.status === 409) {
          // Brouillon purgé ou déjà commandé : on repart d'une grille neuve.
          this.state.result = null;
          this.#save();
          if (this.ui.step === 4 && this.studio.open) this.#make();
        } else if (
          reply.status === 422 &&
          reply.data.error === "excluded_words"
        ) {
          const failure = this.body.querySelector("[data-failure]");
          if (failure && this.ui.step === 4) this.#showFailure(reply, false);
        }
        this.#writeProperties();
        if (this.ui.step === 4 && this.studio.open) this.#showPreview();
        this.#renderEntry();
        this.#updateFoot();
      };
      this.ui.rerender = run().finally(() => {
        this.ui.rerender = null;
        // Encore modifié pendant l'appel : un aperçu de plus. Un échec, lui, ne
        // relance rien : « Ajouter au panier » réessaiera.
        if (this.#hasCard() && this.#textKey() !== textKey) this.#rerender();
      });
      return this.ui.rerender;
    }

    #showFailure(reply, withMessage) {
      const failure = this.body.querySelector("[data-failure]");
      const heading = `<h2 class="cp-title" id="${this.headingId}-failure" tabindex="-1">`;
      const { status, data } = reply;
      let html;
      if (status === 422 && data.error === "excluded_words") {
        const words = data.words ?? [];
        this.state.refused = words;
        this.#save();
        const inWords = this.#refusedIn(this.state.words).length > 0;
        const inHidden = this.#refusedIn(this.#hiddenTokens()).length > 0;
        const inTexts =
          !inWords && !inHidden && this.state.format === "card-a5";
        html = `${heading}Un mot ne passe pas</h2>
          <p class="cp-lead">Ces mots ne sont pas acceptés sur nos cartes : <strong>${esc(words.join(", "))}</strong>.</p>
          ${
            inTexts
              ? `<div class="cp-texts">
                  <div class="cp-text">
                    <label for="${this.headingId}-fix-title">Titre</label>
                    <input class="field cp-field" id="${this.headingId}-fix-title" type="text" maxlength="${MAX_TEXT}" autocomplete="off" value="${esc(this.state.title)}" data-fix-text="title">
                  </div>
                  <div class="cp-text">
                    <label for="${this.headingId}-fix-signature">Signature</label>
                    <input class="field cp-field" id="${this.headingId}-fix-signature" type="text" maxlength="${MAX_TEXT}" autocomplete="off" value="${esc(this.state.signature)}" data-fix-text="signature">
                  </div>
                </div>`
              : ""
          }
          <div class="cp-failure__actions">
            ${inWords ? '<button class="btn-small glass glass--white cp-btn" type="button" data-fix="words">Modifier mes mots</button>' : ""}
            ${inHidden ? '<button class="btn-small glass cp-btn" type="button" data-fix="longer">Modifier le message</button>' : ""}
            ${inTexts ? '<button class="btn-small glass glass--white cp-btn" type="button" data-fix="texts">Réessayer</button>' : ""}
          </div>`;
      } else if (status === 422 && data.error === "unsatisfiable") {
        const a6 = this.state.format === "postcard-a6";
        html = withMessage
          ? `${heading}Ton message ne tombe pas juste</h2>
            <p class="cp-lead">Tes mots et ton message doivent remplir la grille exactement. Cette fois, ça ne tombe pas juste. Trois façons d’y arriver :</p>
            <div class="cp-failure__actions">
              <button class="btn-small glass glass--white cp-btn" type="button" data-fix="longer">Allonger le message</button>
              <button class="btn-small glass cp-btn" type="button" data-fix="words">Retirer un mot</button>
              <button class="cp-text-button" type="button" data-fix="none">Continuer sans message</button>
            </div>`
          : `${heading}Tes mots ne rentrent pas</h2>
            <p class="cp-lead">La grille n’arrive pas à les placer tous. Retire un mot, de préférence un long${a6 ? ", ou passe à la carte à offrir, plus grande" : ""}.</p>
            <div class="cp-failure__actions">
              <button class="btn-small glass glass--white cp-btn" type="button" data-fix="words">Retirer un mot</button>
              ${a6 ? '<button class="btn-small glass cp-btn" type="button" data-fix="a5">Passer à la carte à offrir</button>' : ""}
            </div>`;
      } else {
        html = `${heading}La carte n’a pas pu être fabriquée</h2>
          <p class="cp-lead">${status === 429 ? MESSAGES.tooMany : MESSAGES.failed}</p>
          <div class="cp-failure__actions">
            ${status === 429 ? "" : '<button class="btn-small glass glass--white cp-btn" type="button" data-fix="retry">Réessayer</button>'}
          </div>`;
      }
      failure.innerHTML = html;
      this.#show("failure");
      this.body
        .querySelector(`#${this.headingId}-failure`)
        ?.focus({ preventScroll: true });
      failure.onclick = (event) => {
        const fix = event.target.closest("[data-fix]")?.dataset.fix;
        if (!fix) return;
        if (fix === "words") this.#goTo(2);
        if (fix === "longer") {
          this.#goTo(3);
          this.body.querySelector(`#${this.headingId}-hidden`)?.focus();
        }
        if (fix === "texts") {
          for (const input of failure.querySelectorAll("[data-fix-text]")) {
            this.state[input.dataset.fixText] = input.value;
          }
          this.state.refused = [];
          this.#save();
          // La grille existe déjà : un nouvel aperçu suffit. Sinon, on la fabrique.
          if (this.#hasCard()) {
            this.#goTo(4);
          } else {
            this.#make();
          }
        }
        if (fix === "none") {
          this.state.hidden = "";
          this.#save();
          this.#goTo(4);
        }
        if (fix === "a5") {
          this.state.format = "card-a5";
          this.#save();
          this.#goTo(4);
        }
        if (fix === "retry") this.#make();
      };
    }

    /* ---- Panier ------------------------------------------------------ */

    async #addToCart() {
      if (!this.#hasCard()) return;
      const primary = this.querySelector("[data-primary]");
      primary.disabled = true;
      // Ce qui part au panier est ce que l'acheteur voit : un titre tapé à
      // l'instant attend son aperçu.
      clearTimeout(this.ui.textTimer);
      if (!this.#isUpToDate()) await this.#rerender();
      this.#writeProperties();
      if (!this.#isUpToDate()) {
        this.#updateFoot();
        this.#renderEntry();
        return;
      }
      // Une carte, une ligne : la nouvelle version remplace l'ancienne. Si le
      // retrait échoue, on n'ajoute rien, sinon la carte serait payée deux fois.
      if (this.state.inCart) {
        try {
          await this.#removeFromCart(this.state.inCart.id);
        } catch {
          this.#updateFoot();
          this.#renderEntry();
          return;
        }
        this.state.inCart = null;
        this.#save();
      }
      this.studio.close();
      // Le formulaire d'Horizon fait le reste : tiroir panier, animations, analytics.
      this.#form()?.requestSubmit();
    }

    async #cartItems() {
      const root = window.Theme?.routes?.cart_url ?? "/cart";
      const response = await fetch(`${root}.js`, {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) throw new Error(`cart_${response.status}`);
      return (await response.json()).items ?? [];
    }

    #cartItemOf(items, id) {
      return id
        ? items.find((item) => item.properties?._puzzle_id === id)
        : undefined;
    }

    async #removeFromCart(id) {
      const item = this.#cartItemOf(await this.#cartItems(), id);
      if (!item) return;
      const change = window.Theme?.routes?.cart_change_url ?? "/cart/change";
      const response = await fetch(`${change}.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ id: item.key, quantity: 0 }),
      });
      if (!response.ok) throw new Error(`cart_change_${response.status}`);
    }

    /**
     * Le panier fait foi. Retirée depuis le tiroir, la carte redevient « prête » ;
     * ajoutée ailleurs (autre onglet, ancienne version du studio), elle est
     * reconnue à son `_puzzle_id`.
     */
    async #syncCart() {
      if (!this.state.inCart && !this.state.result) return;
      let items;
      try {
        items = await this.#cartItems();
      } catch {
        return;
      }
      const { inCart, result } = this.state;
      const item =
        this.#cartItemOf(items, inCart?.id) ??
        this.#cartItemOf(items, result?.id);
      const next = item
        ? { id: item.properties._puzzle_id, variantId: item.variant_id }
        : null;
      if (JSON.stringify(next) === JSON.stringify(inCart)) return;
      // Retrouvée au panier : le studio reprend sa livraison.
      if (item && !inCart) this.state.variantId = item.variant_id;
      this.state.inCart = next;
      this.#save();
      this.#writeProperties();
      this.#renderEntry();
      if (this.studio.open) this.#updateFoot();
    }
  }

  if (!customElements.get("custom-puzzle-creator")) {
    customElements.define("custom-puzzle-creator", CustomPuzzleCreator);
  }
})();
